// src/pages/business/businessPage/BusinessLandingPage.tsx
import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import { motion, useAnimation } from "framer-motion";
import {
  Container,
  Typography,
  Box,
  OutlinedInput,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
} from "@mui/material";
import { PersonOutline, ChevronLeft, ChevronRight, CalendarMonthOutlined, TuneOutlined, GroupsOutlined, NotificationsActiveOutlined, CreditCardOutlined, LinkOutlined, BusinessOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../../../components/auth/auth-context";
import { useIsMobile } from "../../../util/utils";
import { getLoginDestination } from '../../../util/authUtils';

const BusinessLandingPage: React.FC = () => {
  const userContext = useContext(UserContext);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { search } = useLocation();

  const primaryCta = "Create your booking page";

  // Email capture
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);

  // Carousel
  const controls = useAnimation();
  const offsetRef = useRef(0);
  const CARD_W = 300;
  const GAP = 16;
  const STEP = CARD_W + GAP;

  const features = useMemo(() => [
    { title: "Easy scheduling", description: "Share your availability and let people book a time that works.", icon: CalendarMonthOutlined, alt: "Easy scheduling" },
    { title: "Your booking types", description: "Set appointment lengths, service details, prices, and availability.", icon: TuneOutlined, alt: "Customizable booking types" },
    { title: "Team calendars", description: "Assign bookings to team members and keep schedules organized.", icon: GroupsOutlined, alt: "Team scheduling" },
    { title: "Booking reminders", description: "Keep hosts and attendees informed about their appointments.", icon: NotificationsActiveOutlined, alt: "Appointment reminders" },
    { title: "Online payments", description: "Connect a payment provider to accept payments for eligible bookings.", icon: CreditCardOutlined, alt: "Booking payments" },
    { title: "Personal booking pages", description: "Give customers a link to your services, events, or appointments.", icon: LinkOutlined, alt: "Shareable booking pages" },
    { title: "Multiple workspaces", description: "Manage separate teams or businesses from one account.", icon: BusinessOutlined, alt: "Workspace management" },
  ], []);

  const count = features.length;
  const dx = -STEP * count;

  useEffect(() => {
    controls.set({ x: offsetRef.current });
    controls.start({
      x: [offsetRef.current, offsetRef.current + dx],
      transition: { repeat: Infinity, duration: count * 10, ease: "linear" },
    });
  }, [controls, dx, count]);

  const handleNext = () => {
    controls.stop();
    let nextOffset = offsetRef.current - STEP;
    if (nextOffset < dx) nextOffset -= dx;
    offsetRef.current = nextOffset;
    controls.set({ x: offsetRef.current });
    controls.start({
      x: [offsetRef.current, offsetRef.current + dx],
      transition: { repeat: Infinity, duration: count * 10, ease: "linear" },
    });
  };

  const handlePrev = () => {
    controls.stop();
    let prevOffset = offsetRef.current + STEP;
    if (prevOffset > 0) prevOffset += dx;
    offsetRef.current = prevOffset;
    controls.set({ x: offsetRef.current });
    controls.start({
      x: [offsetRef.current, offsetRef.current + dx],
      transition: { repeat: Infinity, duration: count * 10, ease: "linear" },
    });
  };

  const goCreate = () => navigate('/workspaces');
  const goMyAccount = () => navigate(getLoginDestination(userContext?.providerUser));
  const accountLabel = "Open your workspace";

  const handleEmailSubmit = () => {
    if (/^\S+@\S+\.\S+$/.test(email)) {
      const params = new URLSearchParams(search);
      params.set("email", email);
      navigate(`/signup?${params.toString()}`);
    } else {
      setError(true);
    }
  };

  const firstName = userContext?.providerUser?.username?.split(" ")[0] || null;
  const H1 = "Scheduling that makes room for people.";
  const H2 = "Share your availability. Let people book. Keep your day organized.";
  const heroEyebrow = "Your time, simplified";
  const brandName = "Timely365";

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary" }}>
      {/* Hero */}
      <Box
        sx={{
          minHeight: isMobile ? "auto" : "520px",
          mt: userContext?.providerUser ? "40px" : "30px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
            <Typography
              variant="overline"
              sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.08em" }}
            >
              {heroEyebrow}
            </Typography>
            <Typography variant="h3" sx={{ mb: 1 }}>
              {firstName ? `Welcome back, ${firstName}` : brandName}
            </Typography>

            <Typography component="h1" variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {H1}
            </Typography>
            <Typography variant="h6" sx={{ color: "text.secondary" }}>
              {H2}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1.5, color: "text.secondary", maxWidth: 720 }}
            >
              Appointment booking, team availability, and reminders, all in one place.

            </Typography>

            {/* Email capture (only when logged out) */}
            {!userContext?.providerUser?.userId && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mt: 3,
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: { xs: "center", sm: "flex-start" },
                }}
              >
                <OutlinedInput
                  value={email}
                  type="email"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(false);
                  }}
                  placeholder="Enter your email"
                  inputProps={{ "aria-label": "Your email address" }}
                  error={error}
                  fullWidth
                />
                <Button
                  onClick={handleEmailSubmit}
                  variant="contained"
                  startIcon={<PersonOutline />}
                  sx={{ minWidth: 180, height: 52 }}
                >
                  Get started
                </Button>
              </Box>
            )}

            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {!userContext?.providerUser?.userId && <Button onClick={() => navigate('/login')} variant="outlined">Sign in</Button>}
              <Button onClick={() => navigate('/booking')}>Find a booking page</Button>
            </Box>
            {/* Account / Create profile */}
            {userContext?.providerUser?.defaultBusinessId ? (
              <Box sx={{ mt: 3 }}>
                <Button variant="contained" onClick={goMyAccount}>
                  {accountLabel}
                </Button>
              </Box>
            ) : userContext?.providerUser?.userId ? (
              <Box sx={{ mt: 3 }}>
                <Button variant="contained" onClick={goCreate}>
                  {primaryCta}
                </Button>
              </Box>
            ) : null}
          </Box>
        </Container>
      </Box>

      <Divider />

      {/* Features Carousel */}
      <Container maxWidth="lg" sx={{ py: 8, position: "relative" }}>
        <Typography variant="h4" align="center" gutterBottom>
          Made for your next appointment
        </Typography>

        <IconButton
          aria-label="Previous features"
          onClick={handlePrev}
          sx={{
            position: "absolute",
            left: -3,
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.3,
            p: 0.5,
            zIndex: 2,
            "& svg": { fontSize: "2.5rem" },
            "&:hover": { opacity: 0.6 },
          }}
        >
          <ChevronLeft />
        </IconButton>

        <Box sx={{ overflow: "hidden" }}>
          <motion.div
            style={{ display: "flex", width: STEP * count * 2 }}
            animate={controls}
          >
            {[...features, ...features].map((feat, i) => (
              <Box
                key={i}
                sx={{ minWidth: STEP - GAP, mx: 1, flex: "0 0 auto" }}
              >
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ height: 160, display: "grid", placeItems: "center", bgcolor: "action.hover", color: "primary.main" }}>
                    <feat.icon sx={{ fontSize: 64 }} aria-hidden="true" />
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {feat.title}
                    </Typography>
                    <Typography variant="body2">{feat.description}</Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </motion.div>
        </Box>

        <IconButton
          aria-label="Next features"
          onClick={handleNext}
          sx={{
            position: "absolute",
            right: -3,
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.3,
            p: 0.5,
            zIndex: 2,
            "& svg": { fontSize: "2.5rem" },
            "&:hover": { opacity: 0.6 },
          }}
        >
          <ChevronRight />
        </IconButton>
      </Container>
    </Box>
  );
};

export default BusinessLandingPage;
